/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { useQuery } from '@apollo/client';

import logger from 'js/app/loggerSingleton';
import { FormattedHTMLMessage, FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button, Typography2 } from '@coursera/cds-core';
import type { ButtonName } from '@coursera/event-pulse-types';
import { Link } from '@coursera/event-pulse/react';

import EnrollmentReasonCode from 'bundles/enroll-course/common/EnrollmentReasonCode';
import CourseEnrollmentConfirmation from 'bundles/enroll-course/components/CourseEnrollmentConfirmation';
import QueryUpgradeS12n from 'bundles/enroll-course/components/QueryUpgradeS12n.graphql';
import type {
  FetchLatestVersionS12nQuery,
  FetchLatestVersionS12nQueryVariables,
} from 'bundles/enroll-course/components/__generated__/QueryUpgradeS12n';
import { enrollInCourseForFreePromise } from 'bundles/enroll-course/lib/apiClient';
import { checkSessionsV2Epic } from 'bundles/enroll-course/lib/sessionsV2ExperimentUtils';
import { useCourseEnrollModalData } from 'bundles/enroll/data/usePageData';

import _t from 'i18n!nls/enroll-course';

type PropsFromCaller = {
  reasonCode: keyof typeof EnrollmentReasonCode;
  s12nId?: string;
};

const styles = {
  actionContainer: css`
    margin: 48px 0;
  `,
};

const EnrollmentReasonMessageV2 = ({ reasonCode, s12nId }: PropsFromCaller) => {
  const { course } = useCourseEnrollModalData();

  const [didEnroll, setDidEnroll] = useState(false);

  const { data: upgradeS12nData } = useQuery<FetchLatestVersionS12nQuery, FetchLatestVersionS12nQueryVariables>(
    QueryUpgradeS12n,
    {
      variables: {
        s12nId: s12nId!,
      },
      skip: !s12nId || reasonCode !== EnrollmentReasonCode.SPECIALIZATION_UPGRADE_REQUIRED,
      context: { clientName: 'gatewayGql' },
    }
  );

  if (!reasonCode) {
    return null;
  }

  if (didEnroll) {
    return <CourseEnrollmentConfirmation courseId={course.id} />;
  }

  const renderAlreadyEnrolled = () => {
    return (
      <div>
        <Typography2 component="p">
          <FormattedMessage message={_t('You are already enrolled in {courseName}.')} courseName={course.name} />
        </Typography2>
        <div css={styles.actionContainer}>
          <a href={course.homeLink}>{_t('Go to course home')}</a>
        </div>
      </div>
    );
  };

  const renderAlreadyPurchased = () => {
    const enrollInAlreadyPaidCourse = () => {
      checkSessionsV2Epic(course.id).then(() => {
        enrollInCourseForFreePromise(course.id).then(() => setDidEnroll(true));
      });
    };

    return (
      <div>
        <Typography2 component="p">{_t('Please click the button below to access course materials.')}</Typography2>
        <Button type="button" css={styles.actionContainer} variant="primary" onClick={enrollInAlreadyPaidCourse}>
          {_t('Join course')}
        </Button>
      </div>
    );
  };

  const renderCapstoneLocked = () => {
    return (
      <Typography2 component="div">
        {_t('You can only access this Capstone after completing the courses in the Specialization')}
      </Typography2>
    );
  };

  const renderRegionBlocked = () => {
    return (
      <Typography2 component="div">
        <FormattedHTMLMessage
          message={_t(
            `We apologize for the inconvenience. This course is not available in your region.
          Click <a href="{policyUrl}">here</a> for more information.`
          )}
          policyUrl="https://learner.coursera.help/hc/articles/208280116-International-restrictions"
        />
      </Typography2>
    );
  };

  const renderNoAvailableSession = () => {
    return (
      <Typography2 component="p">
        {_t('There is no upcoming session for this course. Please check back later.')}
      </Typography2>
    );
  };

  const renderSpecializationUpgradeRequired = () => {
    const newS12nId = upgradeS12nData?.Specialization?.queryLatestVersionByIds?.[0]?.id;
    return (
      <div>
        <Typography2 component="p">
          {_t(
            'You are currently enrolled in an old version of the specialization. Upgrade the specialization to continue with your purchase.'
          )}
        </Typography2>

        {s12nId && newS12nId && (
          <div css={styles.actionContainer}>
            <Link
              trackingData={{
                button: {
                  linkURL: `/learn/specialization/upgrade/${s12nId}:${newS12nId}`,
                  linkName: 'upgrade_s12n_from_enrollment_error',
                  linkType: 'internal',
                  name: 'upgrade_s12n_from_enrollment_error' as ButtonName,
                },
              }}
              variant="standard"
              href={`/learn/specialization/upgrade/${s12nId}:${newS12nId}`}
            >
              {_t('View Upgrade')}
            </Link>
          </div>
        )}
      </div>
    );
  };

  const renderUnknownReasonCode = () => {
    return (
      <div>
        <Typography2 component="p">
          <FormattedMessage
            message={_t('Sorry, we could not find any enrollment option for {courseName} at this time.')}
            courseName={course.name}
          />
        </Typography2>
        <div css={styles.actionContainer}>
          <a href={course.link}>{_t('Go to course')}</a>
        </div>
      </div>
    );
  };

  // TODO remove duplicate definitions between here and
  // bundles/enroll/models/EnrollmentChoices.ts
  switch (reasonCode) {
    case EnrollmentReasonCode.ENROLLED:
      return renderAlreadyEnrolled();
    case EnrollmentReasonCode.PURCHASED_SINGLE_COURSE:
    case EnrollmentReasonCode.SPECIALIZATION_BULK_PAID:
    case EnrollmentReasonCode.SPECIALIZATION_SUBSCRIBED:
      return renderAlreadyPurchased();
    case EnrollmentReasonCode.CAPSTONE_ACCESS_LOCKED:
      return renderCapstoneLocked();
    case EnrollmentReasonCode.REGION_BLOCKED:
      return renderRegionBlocked();
    case EnrollmentReasonCode.NO_AVAILABLE_SESSION:
      return renderNoAvailableSession();
    case EnrollmentReasonCode.SPECIALIZATION_UPGRADE_REQUIRED:
      return renderSpecializationUpgradeRequired();
    default:
      logger.error(`Unrecognized enrollment reason code: ${reasonCode}`);
      return renderUnknownReasonCode();
  }
};

export default EnrollmentReasonMessageV2;
